import { useState, useEffect } from "react";
import {
  Store, Phone, Mail, MapPin, Clock, CheckCircle, Camera,
  Edit3, Save, X, Shield, AlertTriangle, Navigation, Loader2
} from "lucide-react";
import { pharmacieApi } from "./services/api";

const JOURS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const JOURS_LABELS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function horairesVides() {
  const h = {};
  JOURS.forEach((j) => { h[j] = { ouverture: "", fermeture: "", ferme: true }; });
  return h;
}

function parseHoraires(raw) {
  if (!raw) return horairesVides();
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const h = horairesVides();
    JOURS.forEach((j) => {
      const jour = parsed[j];
      if (jour && (jour.ouverture || jour.fermeture)) {
        h[j] = { ouverture: jour.ouverture || "", fermeture: jour.fermeture || "", ferme: false };
      }
    });
    return h;
  } catch {
    return horairesVides();
  }
}

// ── Composants ───────────────────────────────────────────────────────────────

const StatutBadge = ({ statut }) => {
  const cfg = {
    ACTIVE:     { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500", label: "Active" },
    VALIDEE:    { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500", label: "Active" },
    EN_ATTENTE: { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   dot: "bg-amber-500",  label: "En attente" },
    SUSPENDUE:  { bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200",      dot: "bg-red-500",    label: "Suspendue" },
  }[statut] ?? { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-200", dot: "bg-gray-400", label: statut || "—" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ── Page principale ──────────────────────────────────────────────────────────

export default function MaPharmacie() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [pharmacieId, setPharmacieId] = useState(null);
  const [pharmacie, setPharmacie] = useState(null);
  const [form, setForm] = useState(null);
  const [horaires, setHoraires] = useState(horairesVides());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await pharmacieApi.getMine();
        const mine = Array.isArray(list) ? list[0] : list;
        if (!mine) {
          if (!cancelled) setError("Aucune pharmacie associée à ce compte.");
          return;
        }
        if (cancelled) return;
        setPharmacieId(mine.id);
        setPharmacie(mine);
        setForm({
          nom: mine.nom || "",
          telephone: mine.telephone || "",
          email: mine.email || "",
          adresseComplete: mine.adresseComplete || "",
          quartier: mine.quartier || "",
        });
        setHoraires(parseHoraires(mine.horaires));
      } catch (e) {
        if (!cancelled) setError(e.message || "Erreur de chargement.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleHoraireChange = (jour, field, value) => {
    setHoraires((h) => ({ ...h, [jour]: { ...h[jour], [field]: value } }));
  };

  const handleToggleFerme = (jour) => {
    setHoraires((h) => ({
      ...h,
      [jour]: h[jour].ferme
        ? { ouverture: "08:00", fermeture: "20:00", ferme: false }
        : { ouverture: "", fermeture: "", ferme: true },
    }));
  };

  const handleCancel = () => {
    if (pharmacie) {
      setForm({
        nom: pharmacie.nom || "",
        telephone: pharmacie.telephone || "",
        email: pharmacie.email || "",
        adresseComplete: pharmacie.adresseComplete || "",
        quartier: pharmacie.quartier || "",
      });
      setHoraires(parseHoraires(pharmacie.horaires));
    }
    setEditMode(false);
  };

  const handleSave = async () => {
    if (!pharmacieId) return;
    setSaving(true);
    setError(null);
    try {
      const horairesPayload = {};
      JOURS.forEach((j) => {
        if (!horaires[j].ferme) {
          horairesPayload[j] = { ouverture: horaires[j].ouverture, fermeture: horaires[j].fermeture };
        }
      });
      const updated = await pharmacieApi.updateMine(pharmacieId, {
        nom: form.nom,
        telephone: form.telephone,
        email: form.email,
        adresseComplete: form.adresseComplete,
        quartier: form.quartier,
        horaires: JSON.stringify(horairesPayload),
      });
      setPharmacie(updated);
      setHoraires(parseHoraires(updated.horaires));
      setEditMode(false);
    } catch (e) {
      setError(e.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto flex items-center justify-center h-64">
        <Loader2 size={28} className="animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error && !pharmacie) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3">
          <AlertTriangle size={20} className="text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ma Pharmacie</h1>
          <p className="text-sm text-gray-500 mt-0.5">Informations visibles par les patients sur l'application</p>
        </div>
        {!editMode ? (
          <button onClick={() => setEditMode(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors">
            <Edit3 size={15} /> Modifier
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={handleCancel} disabled={saving}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50">
              <X size={15} /> Annuler
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Enregistrer
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── Colonne gauche ── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Infos principales */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                <Store size={15} className="text-emerald-600" /> Informations générales
              </h2>
              <StatutBadge statut={pharmacie?.statut} />
            </div>

            {/* Photo placeholder */}
            <div className="w-full h-28 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-dashed border-emerald-200 rounded-xl flex items-center justify-center mb-5 cursor-pointer hover:border-emerald-400 transition-colors group">
              <div className="text-center">
                <Camera size={22} className="text-emerald-400 mx-auto mb-1 group-hover:text-emerald-600" />
                <p className="text-xs text-emerald-500 font-medium">Ajouter une photo de la pharmacie</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Nom */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Nom de la pharmacie</label>
                {editMode ? (
                  <input name="nom" value={form.nom} onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-emerald-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                ) : (
                  <p className="text-sm font-bold text-gray-900">{form.nom || "—"}</p>
                )}
              </div>

              {/* Téléphone */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Téléphone</label>
                {editMode ? (
                  <input name="telephone" value={form.telephone} onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-emerald-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                ) : (
                  <p className="text-sm text-gray-800 flex items-center gap-1.5">
                    <Phone size={13} className="text-emerald-500" /> {form.telephone || "—"}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Email</label>
                {editMode ? (
                  <input name="email" value={form.email} onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-emerald-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                ) : (
                  <p className="text-sm text-gray-800 flex items-center gap-1.5">
                    <Mail size={13} className="text-emerald-500" /> {form.email || "—"}
                  </p>
                )}
              </div>

              {/* Adresse */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">Adresse</label>
                {editMode ? (
                  <input name="adresseComplete" value={form.adresseComplete} onChange={handleChange}
                    className="w-full px-3 py-2.5 border border-emerald-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                ) : (
                  <p className="text-sm text-gray-800 flex items-center gap-1.5">
                    <MapPin size={13} className="text-emerald-500" /> {form.adresseComplete || "—"}
                  </p>
                )}
              </div>

              {/* Commune / Quartier */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Commune</label>
                  <p className="text-sm text-gray-800">{pharmacie?.communeNom || "—"}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Quartier</label>
                  {editMode ? (
                    <input name="quartier" value={form.quartier} onChange={handleChange}
                      className="w-full px-3 py-2 border border-emerald-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200" />
                  ) : (
                    <p className="text-sm text-gray-800">{form.quartier || "—"}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Horaires */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <Clock size={15} className="text-emerald-600" /> Horaires d'ouverture
            </h2>
            <div className="space-y-2">
              {JOURS.map((j, i) => {
                const h = horaires[j];
                return (
                  <div key={j} className={`flex items-center justify-between py-2.5 px-3 rounded-xl gap-3 ${h.ferme ? "bg-gray-50" : "bg-emerald-50/50"}`}>
                    <span className={`text-sm font-semibold w-24 flex-shrink-0 ${h.ferme ? "text-gray-400" : "text-gray-800"}`}>
                      {JOURS_LABELS[i]}
                    </span>

                    {editMode ? (
                      <div className="flex items-center gap-2 flex-1 justify-end">
                        {!h.ferme && (
                          <>
                            <input type="time" value={h.ouverture}
                              onChange={(e) => handleHoraireChange(j, "ouverture", e.target.value)}
                              className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs" />
                            <span className="text-gray-400 text-xs">–</span>
                            <input type="time" value={h.fermeture}
                              onChange={(e) => handleHoraireChange(j, "fermeture", e.target.value)}
                              className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs" />
                          </>
                        )}
                        <button type="button" onClick={() => handleToggleFerme(j)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            h.ferme ? "bg-gray-200 text-gray-600" : "bg-red-50 text-red-500 hover:bg-red-100"
                          }`}>
                          {h.ferme ? "Fermé" : "Fermer"}
                        </button>
                      </div>
                    ) : h.ferme ? (
                      <span className="text-xs text-gray-400 font-medium">Fermé</span>
                    ) : (
                      <span className="text-sm text-emerald-700 font-semibold">
                        {h.ouverture} – {h.fermeture}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Colonne droite ── */}
        <div className="space-y-5">

          {/* Visibilité sur l'app */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
              <CheckCircle size={15} className="text-emerald-600" /> Visibilité
            </h2>
            <div className="space-y-3">
              {[
                { label: "Visible sur la carte",   ok: pharmacie?.statut === "ACTIVE" || pharmacie?.statut === "VALIDEE" },
                { label: "Téléphone affiché",      ok: !!form?.telephone },
                { label: "Itinéraire GPS activé",  ok: !!(pharmacie?.latitude && pharmacie?.longitude) },
              ].map(({ label, ok }) => (
                <div key={label} className="flex items-center gap-2.5">
                  {ok
                    ? <CheckCircle size={15} className="text-emerald-500 flex-shrink-0" />
                    : <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />
                  }
                  <span className="text-xs font-medium text-gray-700">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Localisation GPS */}
          {pharmacie?.latitude && pharmacie?.longitude && (
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-3">
                <Navigation size={15} className="text-emerald-600" /> Localisation GPS
              </h2>
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100 mb-3">
                <p className="text-xs text-emerald-600 font-medium">Coordonnées enregistrées</p>
                <p className="text-xs text-gray-700 mt-1 font-mono">
                  {pharmacie.latitude}, {pharmacie.longitude}
                </p>
              </div>
              <a href={`https://www.google.com/maps?q=${pharmacie.latitude},${pharmacie.longitude}`}
                 target="_blank" rel="noreferrer"
                 className="w-full flex items-center justify-center gap-2 py-2.5 border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                <MapPin size={13} /> Voir sur Google Maps
              </a>
            </div>
          )}

          {/* Sécurité */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-3">
              <Shield size={15} className="text-emerald-600" /> Sécurité des données
            </h2>
            <p className="text-xs text-gray-500 leading-relaxed">
              Vos informations sont chiffrées et hébergées sur des serveurs sécurisés.
              Aucune donnée n'est partagée avec des tiers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
